'use client';

import clsx from 'classnames';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Button, Checkbox, FieldHint, TextArea, TextField } from 'ui';
import { emailPattern } from 'utils';

import { useTranslation } from '@/i18n/client';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';

import { sendContactForm } from '../../api';
import styles from './ContactForm.module.scss';
import { ContactFormData } from './types';

export function ContactForm() {
  const locale = useCurrentLocale();
  const { t } = useTranslation(['contact', 'common']);
  const [isLoading, setIsLoading] = useState(false);
  const [formSent, setFormSent] = useState(false);
  const searchParams = useSearchParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<ContactFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsLoading(true);

    const result = await sendContactForm(data);

    if (!result) {
      setIsLoading(false);
      return;
    }

    reset();
    setIsLoading(false);
    setFormSent(true);
  };

  const buttonContainerClassNames = clsx(styles.contact__form__btn__container, {
    [styles['contact__form__btn__container--sent']]: !!formSent,
  });

  useEffect(() => {
    const field = searchParams?.get('field');

    if (!field || field !== 'name') {
      window.document.scrollingElement?.scrollTo(0, 0);
      return;
    }

    const nameInput = document.getElementsByClassName(
      'nameInput'
    )[0] as HTMLInputElement;

    nameInput.scrollIntoView({ block: 'center' });
    nameInput.focus({ preventScroll: true });
  }, []);

  return (
    <>
      <div className={styles.contact__form}>
        <form
          className={styles.contact__form__controls}
          onSubmit={handleSubmit(onSubmit)}
        >
          <h2>{t('contactForm.title')}</h2>
          <div className={styles.controls__row}>
            <div className={styles.controls__row__input}>
              <TextField
                {...register('name', {
                  required: t('contactForm.fieldRequired') || '',
                  validate: (value) => {
                    const errorMessage = t('contactForm.invalidFormat');
                    if (value.length < 3) {
                      return errorMessage;
                    }
                  },
                })}
                hasError={!!errors.name}
                isValid={!errors.name && dirtyFields.name}
                label={t('common:common.firstName')}
                inputClassName="nameInput"
              />
              {errors.name ? (
                <FieldHint isError>{errors.name?.message}</FieldHint>
              ) : null}
            </div>
          </div>
          <div className={styles.controls__row}>
            <div className={styles.controls__row__input}>
              <TextField
                {...register('email', {
                  required: t('contactForm.fieldRequired') || '',
                  pattern: {
                    value: emailPattern,
                    message: t('contactForm.invalidEmail') || '',
                  },
                })}
                hasError={!!errors.email}
                isValid={!errors.email && dirtyFields.email}
                label={t('common:common.emailAddress')}
              />
              {errors.email ? (
                <FieldHint isError>{errors.email?.message}</FieldHint>
              ) : null}
            </div>
            <div className={styles.controls__row__input}>
              <TextField
                {...register('phoneNumber', {
                  required: t('contactForm.fieldRequired') || '',
                  validate: (value) => {
                    const errorMessage = t('contactForm.invalidPhone') || '';

                    if (value?.startsWith('00') && value.length !== 13) {
                      return errorMessage;
                    }

                    if (value?.startsWith('+') && value.length !== 12) {
                      return errorMessage;
                    }

                    if (value.length !== 9) {
                      return errorMessage;
                    }
                  },
                })}
                hasError={!!errors.phoneNumber}
                isValid={!errors.phoneNumber && dirtyFields.phoneNumber}
                label={t('common:common.phoneNumber')}
              />
              {errors.phoneNumber ? (
                <FieldHint isError>{errors.phoneNumber?.message}</FieldHint>
              ) : null}
            </div>
          </div>
          <div className={styles.controls__row}>
            <div className={styles.controls__row}>
              <TextArea
                {...register('message', {
                  required: t('contactForm.fieldRequired') || '',
                  validate: (value) => {
                    const errorMessage = t('contactForm.invalidMessage') || '';

                    if (value.length < 3) {
                      return errorMessage;
                    }
                  },
                })}
                rows={4}
                hasError={!!errors.message}
                label={t('common:common.messageContent')}
              />
              {errors.message ? (
                <FieldHint isError>{errors.message?.message}</FieldHint>
              ) : null}
            </div>
          </div>
          <div className={styles.controls__row}>
            <div className={styles.controls__row}>
              <div className={styles.controls__checkbox}>
                <Checkbox
                  {...register('compliance', {
                    required: t('contactForm.fieldRequired') || '',
                  })}
                  hasError={!!errors.compliance}
                />
                <div>
                  <Trans
                    i18nKey="contact:contactForm.compliance"
                    t={t}
                    components={[
                      <a key={0} href={routes[locale].RULES} target="_blank" />,
                      <a
                        key={1}
                        href={routes[locale].PRIVACY_POLICY}
                        target="_blank"
                      />,
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={buttonContainerClassNames}>
            <Button
              variant="blue"
              aria-label="Submit form"
              type="submit"
              isLoading={isLoading}
              isDisabled={
                isLoading || formSent || Object.keys(errors).length > 0
              }
              buttonName="sendButton"
            >
              {formSent
                ? t('contactForm.thankYouMassage')
                : t('common:common.send')}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
